import React from 'react';
import { Link, useParams, Navigate } from 'react-router-dom';
import { ArrowLeft, ArrowUpRight, MapPin, Building2, Layers, PoundSterling, Calendar } from 'lucide-react';
import Reveal from '../components/Reveal';
import { PROJECTS } from '../data/mock';

export default function ProjectDetail() {
  const { id } = useParams();
  const idx = PROJECTS.findIndex((p) => p.id === id);
  if (idx === -1) return <Navigate to="/projects" replace />;
  const p = PROJECTS[idx];
  const next = PROJECTS[(idx + 1) % PROJECTS.length];

  return (
    <div className="bg-[var(--bg)]">
      {/* Hero image */}
      <section className="relative bg-[var(--navy)] text-white">
        <div className="absolute inset-0">
          <img src={p.image} alt={p.title} className="w-full h-full object-cover opacity-60" />
          <div className="absolute inset-0 bg-gradient-to-t from-[var(--navy)] via-[var(--navy)]/40 to-[var(--navy)]/40" />
        </div>
        <div className="relative container-rs pt-44 pb-24">
          <Link to="/projects" className="font-tag inline-flex items-center gap-2 opacity-80 hover:opacity-100"><ArrowLeft size={14} /> All projects</Link>
          <div className="font-tag mt-8 opacity-80">{p.sector} · {p.role}</div>
          <h1 className="font-display text-6xl md:text-9xl mt-3 leading-[0.92]">{p.title}<span className="text-[var(--coral)]">.</span></h1>
          <div className="mt-6 flex items-center gap-3 font-tag opacity-90"><MapPin size={14} /> {p.location} · {p.year}</div>
        </div>
      </section>

      {/* Metrics */}
      <section className="py-16 border-b border-[var(--line)]">
        <div className="container-rs grid grid-cols-2 md:grid-cols-4 gap-6">
          <Metric icon={Calendar} l="Year" v={p.year} />
          <Metric icon={Building2} l="Sector" v={p.sector} />
          <Metric icon={Layers} l="Units" v={p.units ?? '—'} />
          <Metric icon={PoundSterling} l="Project value" v={p.value} />
        </div>
      </section>

      {/* Body */}
      <section className="py-24">
        <div className="container-rs grid md:grid-cols-12 gap-10">
          <div className="md:col-span-4">
            <span className="section-index"><span className="coral-dot" /> Brief</span>
            <h2 className="font-display text-4xl md:text-5xl mt-4 leading-[0.95]">A {p.sector.toLowerCase()} <span className="font-display-italic text-[var(--coral)]">delivered.</span></h2>
          </div>
          <div className="md:col-span-7 md:col-start-6">
            <p className="text-[18px] leading-relaxed text-[var(--ink)]/85">{p.summary}</p>
            <p className="mt-6 text-[15px] text-[var(--ink-mute)] leading-relaxed">
              Bespoke acted as {p.role.toLowerCase()} on this scheme, coordinating design teams, sub-contractors and supply chain through a single project director. Delivery was achieved within programme and to specification.
            </p>
            <p className="mt-4 text-[15px] text-[var(--ink-mute)] leading-relaxed">
              The site team adopted our standard quality and safety procedures, including weekly client progress reports, daily site briefings, and a Considerate Constructors monitoring programme.
            </p>
          </div>
        </div>
      </section>

      {/* Gallery */}
      <section className="pb-24">
        <div className="container-rs grid md:grid-cols-12 gap-6">
          <Reveal className="md:col-span-7 overflow-hidden rounded-2xl">
            <img src={p.image} alt="" className="w-full h-[420px] md:h-[560px] object-cover" />
          </Reveal>
          <div className="md:col-span-5 grid gap-6">
            <Reveal className="overflow-hidden rounded-2xl"><img src="https://images.unsplash.com/photo-1599696848652-f0ff23bc911f?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200" alt="" className="w-full h-[270px] object-cover" /></Reveal>
            <Reveal className="overflow-hidden rounded-2xl"><img src="https://images.unsplash.com/photo-1605775024765-5f2c9c55fe4d?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200" alt="" className="w-full h-[270px] object-cover" /></Reveal>
          </div>
        </div>
      </section>

      {/* Next project */}
      <section className="py-24 bg-[var(--navy)] text-white">
        <div className="container-rs grid md:grid-cols-12 gap-10 items-center">
          <div className="md:col-span-7">
            <span className="section-index text-white/70"><span className="coral-dot" /> Next project</span>
            <h3 className="font-display text-5xl md:text-7xl mt-4 leading-[0.95]">{next.title}<span className="font-display-italic text-[var(--coral)]">.</span></h3>
            <div className="mt-3 font-tag opacity-80">{next.sector} · {next.location}</div>
          </div>
          <div className="md:col-span-4 md:col-start-9 flex md:justify-end">
            <Link to={`/projects/${next.id}`} className="pill pill-coral"><span>View</span><ArrowUpRight size={14} /></Link>
          </div>
        </div>
      </section>
    </div>
  );
}

function Metric({ icon: Icon, l, v }) {
  return (
    <div className="flex items-start gap-4">
      <div className="w-11 h-11 rounded-full bg-[var(--coral)]/10 text-[var(--coral)] flex items-center justify-center shrink-0"><Icon size={16} /></div>
      <div>
        <div className="font-tag text-[var(--ink-mute)]">{l}</div>
        <div className="font-display text-2xl mt-1">{v}</div>
      </div>
    </div>
  );
}
